import { createApi } from "@reduxjs/toolkit/query/react";
import { privateBaseQuery } from "../base/private/privateBaseQuery";
import { User } from "@/types/user";
import {
  GetStudentsAsManagerParams,
  GetStudentsAsManagerResponse,
} from "@/types/canonical";
import {
  GetUsersRequestedFields,
  GetStudentsAsManagerFullInfoRequestedFields,
  GetUserEditableProfileRequestedFields,
} from "./userFields";

interface InviteCodeUserThumb {
  big: string | null;
  medium: string | null;
  small: string | null;
}

interface InviteCodeUser {
  id: number;
  photo: string | null;
  photo_thumb: InviteCodeUserThumb | null;
  color: string | null;
  firstname: string | null;
  lastname: string | null;
  is_online: boolean;
  last_activity_date?: string | null;
}

interface InviteCodeUsersPaginationLink {
  url: string | null;
  label: string;
  active: boolean;
}

export interface InviteCodeUsersEntities {
  current_page: number;
  data: InviteCodeUser[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: InviteCodeUsersPaginationLink[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

interface GetInviteCodeUsersResponse {
  status: string;
  status_code: string;
  entities: InviteCodeUsersEntities;
}

export interface DeleteStudentPhotosResponse {
  status: string;
  status_code: string;
  message: string;
  deleted: {
    student_id: number;
    user_id: number;
    deleted_count: number;
  };
}

export interface ResetBiometricResponse {
  status: string;
  status_code: string;
  message: string;
}

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: privateBaseQuery(),
  tagTypes: ["Users"],
  endpoints: (builder) => ({
    getUsers: builder.query<
      GetStudentsAsManagerResponse,
      GetStudentsAsManagerParams
    >({
      query: ({ page = 1, limit = 10, query, orderBy, group }: GetStudentsAsManagerParams) => ({
        url: "/users",
        method: "GET",
        params: {
          page,
          limit,
          // Some backends expect `per_page` instead of `limit`.
          per_page: limit,
          ...(query ? { query } : {}),
          ...(orderBy ? { orderBy } : {}),
          // Server-side group filter (e.g. "proctor"); honored by UserService::filter.
          ...(group ? { group } : {}),
        },
        headers: {
          "X-Requested-Fields": GetUsersRequestedFields.join(","),
        },
      }),
      providesTags: [{ type: "Users", id: "LIST" }],
    }),
    getUsersByInviteCode: builder.query<
      InviteCodeUsersEntities,
      { page?: number; limit?: number; inviteCodeId: number }
    >({
      query: ({ page = 1, limit = 10, inviteCodeId }) => ({
        url: "/users",
        method: "GET",
        params: { page, limit, invite_code_id: inviteCodeId },
        headers: {
          "X-Requested-Fields":
            "id,photo,photo_thumb,color,firstname,lastname,is_online,last_activity_date",
        },
      }),
      transformResponse: (response: GetInviteCodeUsersResponse) =>
        response.entities,
    }),
    getStudents: builder.query<
      GetStudentsAsManagerResponse,
      GetStudentsAsManagerParams
    >({
      query: ({ page = 1, limit = 10, query, orderBy }: GetStudentsAsManagerParams) => ({
        url: "/students",
        method: "GET",
        params: {
          page,
          limit,
          // Some backends expect `per_page` instead of `limit`.
          per_page: limit,
          ...(query ? { query } : {}),
          ...(orderBy ? { orderBy } : {}),
        },
        headers: {
          "X-Requested-Fields":
            GetStudentsAsManagerFullInfoRequestedFields.join(","),
        },
      }),
      providesTags: [{ type: "Users", id: "LIST" }],
      keepUnusedDataFor: 600,
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const safeQuery = queryArgs?.query?.trim() ?? "";
        const safeLimit = queryArgs?.limit ?? 10;
        return `${endpointName}|${safeQuery}|${safeLimit}`;
      },
      merge: (currentCache, newData) => {
        if (!currentCache?.entities?.data) {
          return newData;
        }

        const existing = currentCache.entities.data ?? [];
        const incoming = newData?.entities?.data ?? [];

        const byId = new Map<number, any>();
        for (const item of existing as any[]) {
          if (item?.id != null) byId.set(item.id, item);
        }
        for (const item of incoming as any[]) {
          if (item?.id != null) byId.set(item.id, item);
        }

        return {
          ...newData,
          entities: {
            ...newData.entities,
            data: Array.from(byId.values()),
          },
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const nextQuery = currentArg?.query?.trim() ?? "";
        const prevQuery = previousArg?.query?.trim() ?? "";
        const nextLimit = currentArg?.limit ?? 10;
        const prevLimit = previousArg?.limit ?? 10;
        const nextPage = currentArg?.page ?? 1;
        const prevPage = previousArg?.page ?? 1;
        return (
          nextQuery !== prevQuery ||
          nextLimit !== prevLimit ||
          nextPage !== prevPage
        );
      },
    }),
    getUserById: builder.query<User, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "GET",
        headers: {
          "X-Requested-Fields": GetUsersRequestedFields.join(","),
        },
      }),
    }),
    /**
     * Профиль пользователя со всеми полями UserValidator — то, из чего
     * собирается полный payload для updateUser. Точечная правка одного поля
     * через PUT /users/{id} невозможна: бэкенд затирает всё, чего нет в теле.
     *
     * providesTags намеренно `Users/LIST`: существующий updateUser уже
     * инвалидирует этот тег, так что рефетч происходит без правки мутации.
     */
    getUserEditableProfile: builder.query<User, number>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "GET",
        headers: {
          "X-Requested-Fields": GetUserEditableProfileRequestedFields.join(","),
        },
      }),
      transformResponse: (response: { entity: User }) => response.entity,
      providesTags: [{ type: "Users", id: "LIST" }],
    }),
    updateUser: builder.mutation<User, Partial<User>>({
      query: ({ id, ...body }) => ({
        url: `/users/${id}`,
        method: "PUT",
        data: body,
      }),
      transformResponse: (response: { entity: User }) => response.entity,
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    // Create student user via /students
    createUser: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: "/students",
        method: "POST",
        data: body,
        headers: {
          "X-Requested-Fields": GetStudentsAsManagerFullInfoRequestedFields.join(","),
        },
      }),
      transformResponse: (response: { entity: User }) => response.entity,
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    // Create teacher user via /teachers
    createTeacher: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: "/teachers",
        method: "POST",
        data: body,
      }),
      transformResponse: (response: { entity: User }) => response.entity,
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    updateTeacher: builder.mutation<User, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/teachers/${id}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    updateStudent: builder.mutation<User, { id: number; body: Record<string, unknown> }>({
      query: ({ id, body }) => ({
        url: `/students/${id}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    // Create proctor (or any non-teacher/non-student role) via generic /users.
    // Backend gates `group` server-side: only manager/admin can set arbitrary group;
    // teacher gets coerced to "student". See oqylyq-laravel UsersController::beforeCreate.
    createProctor: builder.mutation<User, Partial<User>>({
      query: (body) => ({
        url: "/users",
        method: "POST",
        data: body,
      }),
      transformResponse: (response: { entity: User }) => response.entity,
      invalidatesTags: [{ type: "Users", id: "LIST" }],
    }),

    deleteStudentPhotos: builder.mutation<DeleteStudentPhotosResponse, { studentId: number }>({
      query: ({ studentId }) => ({
        url: `/students/${studentId}/photos.json`,
        method: "DELETE",
      }),
    }),

    resetBiometric: builder.mutation<ResetBiometricResponse, { subjectId: number }>({
      query: ({ subjectId }) => ({
        url: `/person/by-subject-id/${subjectId}`,
        method: "DELETE",
      }),
    }),
  }),
});

export const {
  useUpdateUserMutation,
  useCreateUserMutation,
  useCreateTeacherMutation,
  useUpdateTeacherMutation,
  useUpdateStudentMutation,
  useCreateProctorMutation,
  useDeleteStudentPhotosMutation,
  useResetBiometricMutation,
  useGetUsersQuery,
  useGetStudentsQuery,
  useGetUserByIdQuery,
  useGetUserEditableProfileQuery,
  useGetUsersByInviteCodeQuery,
} = userApi;
